import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';

const API = process.env.REACT_APP_API_URL || '';
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const MAX_PDF_SIZE = 32 * 1024 * 1024;
const MAX_FILES = 5;

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const PDF_TYPE = 'application/pdf';

function isImage(type) { return IMAGE_TYPES.includes(type); }
function isPdf(type) { return type === PDF_TYPE; }
function isSupported(type) { return isImage(type) || isPdf(type); }
function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function ChatInterface({ messages, input, onInputChange, onSend, onClear, loaded }) {
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState([]);           // File[] staged for next send
  const [dragging, setDragging] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const addFiles = (incoming) => {
    const errors = [];
    const accepted = [];
    for (const f of incoming) {
      if (!isSupported(f.type)) {
        errors.push(`${f.name}: unsupported type`);
        continue;
      }
      const max = isPdf(f.type) ? MAX_PDF_SIZE : MAX_IMAGE_SIZE;
      if (f.size > max) {
        errors.push(`${f.name}: too large (${formatSize(f.size)}, max ${formatSize(max)})`);
        continue;
      }
      accepted.push(f);
    }
    setPending(prev => {
      const combined = [...prev, ...accepted];
      if (combined.length > MAX_FILES) {
        errors.push(`Max ${MAX_FILES} files per message — extras ignored`);
      }
      return combined.slice(0, MAX_FILES);
    });
    setUploadError(errors.join(' · '));
  };

  const onFilePick = (e) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  const removePending = (idx) => {
    setPending(prev => prev.filter((_, i) => i !== idx));
    setUploadError('');
  };

  const handleSend = async () => {
    const text = input.trim();
    if ((!text && pending.length === 0) || sending) return;

    const filesToSend = pending;
    onInputChange('');
    setPending([]);
    setUploadError('');
    setSending(true);
    await onSend(text, filesToSend);
    setSending(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Drag and drop anywhere in the chat container
  const onDragEnter = (e) => {
    e.preventDefault();
    dragCounter.current++;
    if (e.dataTransfer.types?.includes('Files')) setDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragging(false);
    }
  };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragging(false);
    addFiles(Array.from(e.dataTransfer.files || []));
  };

  if (!loaded) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <div className="loading-text">Loading conversation history...</div>
      </div>
    );
  }

  return (
    <div
      className={`chat-container ${dragging ? 'chat-container-dragging' : ''}`}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {dragging && (
        <div className="chat-drop-overlay">
          <div className="chat-drop-message">Drop images or PDFs to attach</div>
        </div>
      )}

      <div className="chat-messages">
        {messages.map((msg, i) => (
          <div key={i}>
            <div className="chat-sender">{msg.role === 'user' ? 'You' : 'Compass'}</div>
            <div className={`chat-message ${msg.role}`}>
              {msg.role === 'assistant' ? (
                <ReactMarkdown>{msg.content}</ReactMarkdown>
              ) : (
                msg.content
              )}
              {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                <div className="chat-attachments">
                  {msg.attachments.map((att, j) => (
                    <AttachmentChip key={j} att={att} />
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div>
            <div className="chat-sender">Compass</div>
            <div className="chat-message assistant">
              <div className="loading-spinner" style={{ width: 20, height: 20, borderWidth: 2 }} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-area">
        {pending.length > 0 && (
          <div className="chat-pending-attachments">
            {pending.map((f, i) => (
              <PendingChip key={i} file={f} onRemove={() => removePending(i)} />
            ))}
          </div>
        )}
        {uploadError && <div className="chat-upload-error">{uploadError}</div>}

        <div className="chat-input-wrapper">
          <button
            type="button"
            className="chat-attach-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || pending.length >= MAX_FILES}
            title="Attach images or PDFs"
          >
            Attach
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp,application/pdf"
            multiple
            style={{ display: 'none' }}
            onChange={onFilePick}
          />
          <textarea
            ref={inputRef}
            className="chat-input"
            placeholder="Ask Compass anything — or drop a pentest PDF, screenshot, etc."
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            disabled={sending}
          />
          <button
            className="chat-send-btn"
            onClick={handleSend}
            disabled={sending || (!input.trim() && pending.length === 0)}
          >
            {sending ? 'Thinking...' : 'Send'}
          </button>
          {messages.length > 1 && (
            <button
              className="refresh-btn"
              onClick={onClear}
              disabled={sending}
              title="Clear conversation"
              style={{ padding: '12px', fontSize: '11px' }}
            >
              Clear
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PendingChip({ file, onRemove }) {
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (isImage(file.type)) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);

  return (
    <div className="chat-pending-chip">
      {preview ? (
        <img src={preview} alt={file.name} className="chat-pending-thumb" />
      ) : (
        <span className="chat-pending-icon">PDF</span>
      )}
      <span className="chat-pending-name" title={file.name}>{file.name}</span>
      <span className="chat-pending-size">{formatSize(file.size)}</span>
      <button type="button" className="chat-pending-remove" onClick={onRemove} title="Remove">×</button>
    </div>
  );
}

function AttachmentChip({ att }) {
  const src = att.filename ? `${API}/api/chat/file/${att.filename}` : null;
  if (att.kind === 'image' && src) {
    return (
      <a href={src} target="_blank" rel="noopener noreferrer" className="chat-attachment-image-link">
        <img src={src} alt={att.originalName} className="chat-attachment-image" />
      </a>
    );
  }
  return (
    <a
      href={src || '#'}
      target="_blank"
      rel="noopener noreferrer"
      className="chat-attachment-file"
      title={att.originalName}
    >
      <span className="chat-attachment-icon">{att.kind === 'document' ? 'PDF' : 'FILE'}</span>
      <span className="chat-attachment-name">{att.originalName}</span>
    </a>
  );
}
